import styled from 'styled-components/macro';
import { getFlex } from '../styleUtils';

interface ButtonWrapperProps {
  as?: any;
  size: 'small' | 'large' | 'default';
  varient: 'primary' | 'secondary' | 'link';
  disabled?: boolean | undefined;
  danger?: boolean | undefined;
  loading?: boolean | undefined;
}

const varients = {
  padding: {
    small: '0.25rem 1rem',
    default: '0.3rem 1.5rem',
    large: '0.6rem 2.5rem',
  },
  fontSize: {
    small: '0.7rem',
    default: '0.9rem',
    large: '1.1rem',
  },
  color: {
    primary: 'var(--color-white)',
    secondary: 'var(--gray-700)',
    link: 'var(--color-primary)',
    muted: 'var(--gray-700)',
  },
  colorHover: {
    primary: 'var(--color-white)',
    secondary: 'var(--color-primary)',
    link: 'var(--indigo-500)',
    muted: 'var(--gray-600)',
  },
  colorActive: {
    primary: 'var(--color-white)',
    secondary: 'var(--indigo-700)',
    link: 'var(--indigo-700)',
    muted: 'var(--gray-800)',
  },
  background: {
    primary: 'var(--color-primary)',
    secondary: 'none',
    link: 'none',
    muted: 'none',
  },
  backgroundHover: {
    primary: 'var(--indigo-500)',
    secondary: 'none',
    link: 'none',
    muted: 'var(--gray-100)',
  },
  backgroundActive: {
    primary: 'var(--indigo-darker)',
    secondary: 'none',
    link: 'none',
    muted: 'var(--coolGray-100)',
  },
  border: {
    primary: 'var(--color-white)',
    secondary: 'var(--coolGray-500)',
    link: 'transparent',
    muted: 'transparent',
  },
  borderHover: {
    primary: 'var(--color-white)',
    secondary: 'var(--color-primary)',
    link: 'none',
    muted: 'transparent',
  },
  borderActive: {
    primary: 'var(--color-white)',
    secondary: 'var(--indigo-700)',
    link: 'transparent',
    muted: 'transparent',
  },
  textShadow: {
    primary: ' 0 -1px 0 rgb(0 0 0 / 12%)',
    secondary: ' 0 -1px 0 rgb(0 0 0 / 12%)',
    link: 'none',
    muted: 'none',
  },
  danger: {
    default: 'var(--rose-500)',
    hover: 'var(--rose-400)',
    active: 'var(--rose-600)',
  },
};

export const ButtonWrapper = styled.button<ButtonWrapperProps>`
  border-radius: 5px;
  appearance: button;
  position: relative;
  display: inline-block;
  cursor: pointer;
  background: ${({ varient }) => varients.background[varient]};
  padding: ${({ size }) => varients.padding[size]};
  font-size: ${({ size }) => varients.fontSize[size]};
  pointer-events: ${({ disabled }) => disabled && 'none'};
  color: ${({ varient }) => varients.color[varient]};
  border: ${({ varient }) => `1px solid ${varients.border[varient]}`};
  box-shadow: ${({ varient }) =>
    ['primary', 'secondary'].includes(varient) && '0 2px 0 rgb(0 0 0 / 5%)'};
  text-shadow: ${({ varient }) => varients.textShadow[varient]};
  transition: box-shadow 150ms var(--ease-in-out-quad),
    color 200ms var(--ease-in-out-quad),
    background-color 200ms var(--ease-in-out-quad);

  // Danger
  color: ${({ danger, varient }) =>
    varient !== 'primary' && danger && varients.danger.default};
  border: ${({ danger, varient }) =>
    varient === 'secondary' &&
    danger &&
    `1px solid ${varients.danger.default}`};

  // Disabled or Loading
  pointer-events: ${({ loading, disabled }) => (loading || disabled) && 'none'};
  user-select: ${({ loading, disabled }) => (loading || disabled) && 'none'};

  filter: ${({ loading }) =>
    loading && 'brightness(0.95) grayscale(5%) opacity(95%)'};
  filter: ${({ disabled }) =>
    disabled && 'brightness(0.9) grayscale(10%) opacity(90%)'};

  span.label {
    ${getFlex('center')};
    gap: 0.5rem;
  }

  &:hover {
    color: ${({ varient }) => varients.colorHover[varient]};
    border: ${({ varient }) => `1px solid ${varients.borderHover[varient]}`};
    background: ${({ varient }) => varients.backgroundHover[varient]};

    // Danger
    color: ${({ danger, varient }) =>
      varient !== 'primary' && danger && varients.danger.hover};
    border: ${({ danger, varient }) =>
      varient === 'secondary' &&
      danger &&
      `1px solid ${varients.danger.hover}`};
  }
  &:active {
    background: ${({ varient }) => varients.backgroundActive[varient]};
    color: ${({ varient }) => varients.colorActive[varient]};
    border: ${({ varient }) => `1px solid ${varients.borderActive[varient]}`};
    box-shadow: none;

    // Danger
    color: ${({ danger, varient }) =>
      varient !== 'primary' && danger && varients.danger.active};
    border: ${({ danger, varient }) =>
      varient === 'secondary' &&
      danger &&
      `1px solid ${varients.danger.active}`};
  }
`;
